/**
 * ADR-206 — the parent-side client for a plugin's declared file tree.
 *
 * Called ONLY by PluginPage's postMessage bridge, on behalf of the sandboxed
 * plugin: the iframe has no network and never sees the JWT. Unlike the generic
 * helpers in client.ts, these never throw on a 4xx — the host answers a
 * person-caused problem with `{error, message}`, and the message is exactly
 * what the plugin must show ("\"Launch\" is already used here."). Turning it
 * into `Error("HTTP 409: …")` would bury it.
 */
import { tokenManager } from '../auth/tokenManager'

export interface FilesResult {
  ok: boolean
  status: number
  data?: unknown
  error?: string
  message?: string
}

/** One upload piece. Small enough for any proxy and the server's read timeout. */
export const UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024

/** The largest file a person may upload (PRD: 1 GB). Mirrors the host's cap. */
export const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024 + 16 * 1024 * 1024

/** The largest file handed to a plugin as bytes (e.g. a PDF to render). */
export const MAX_BYTES_TRANSFER = 100 * 1024 * 1024

export async function authed(path: string, init: RequestInit): Promise<Response> {
  const send = (token: string | null) =>
    fetch(path, { ...init, headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` } })
  let resp = await send(tokenManager.getToken())
  if (resp.status === 401 && (await tokenManager.refresh())) {
    resp = await send(tokenManager.getToken())
  }
  return resp
}

async function toResult(resp: Response): Promise<FilesResult> {
  let body: unknown = null
  try {
    body = await resp.json()
  } catch {
    body = null
  }
  if (resp.ok) return { ok: true, status: resp.status, data: body }
  const b = (body ?? {}) as { error?: string; message?: string }
  return {
    ok: false,
    status: resp.status,
    error: b.error ?? `http_${resp.status}`,
    message:
      b.message ??
      (resp.status === 401
        ? 'Your session has expired. Please sign in again.'
        : resp.status === 403
          ? 'You do not have permission to do that.'
          : 'Something went wrong. Please try again.'),
  }
}

const base = (pluginId: string, scope: string) =>
  `/api/plugins/${encodeURIComponent(pluginId)}/files/${encodeURIComponent(scope)}`

/** POST /api/plugins/:id/files/:scope {op, args}. */
export async function filesOp(
  pluginId: string,
  scope: string,
  op: string,
  args: Record<string, unknown> = {},
): Promise<FilesResult> {
  try {
    const resp = await authed(base(pluginId, scope), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ op, args }),
    })
    return await toResult(resp)
  } catch {
    return { ok: false, status: 0, error: 'network', message: 'Mission Control could not be reached. Check your connection and try again.' }
  }
}

export interface UploadProgress {
  index: number
  name: string
  loaded: number
  total: number
}

/**
 * Upload one file: start, ordered chunks, finish. On ANY failure — including
 * `signal` aborting — the upload is aborted on the host so nothing partial is
 * left (PRD), and the result says so in plain words.
 */
export async function uploadFile(
  pluginId: string,
  scope: string,
  dir: string,
  file: File,
  index: number,
  onProgress: (p: UploadProgress) => void,
  signal: AbortSignal,
): Promise<FilesResult> {
  if (file.size > MAX_UPLOAD_BYTES) {
    return { ok: false, status: 413, error: 'too_large', message: `"${file.name}" is larger than 1 GB.` }
  }
  const start = await filesOp(pluginId, scope, 'upload_start', { path: dir, name: file.name, size: file.size })
  if (!start.ok) return start
  const uploadId = (start.data as { upload_id: string }).upload_id
  const abort = () => filesOp(pluginId, scope, 'upload_abort', { upload_id: uploadId })
  let offset = 0
  try {
    onProgress({ index, name: file.name, loaded: 0, total: file.size })
    while (offset < file.size) {
      if (signal.aborted) {
        await abort()
        return { ok: false, status: 0, error: 'cancelled', message: `Upload of "${file.name}" was cancelled. Nothing was saved.` }
      }
      const piece = file.slice(offset, Math.min(offset + UPLOAD_CHUNK_BYTES, file.size))
      const resp = await authed(`${base(pluginId, scope)}/upload?id=${uploadId}&offset=${offset}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: piece,
        signal,
      })
      const r = await toResult(resp)
      if (!r.ok) {
        await abort()
        return r
      }
      offset += piece.size
      onProgress({ index, name: file.name, loaded: offset, total: file.size })
    }
    const fin = await filesOp(pluginId, scope, 'upload_finish', { upload_id: uploadId })
    if (!fin.ok) await abort()
    return fin
  } catch {
    await abort()
    return {
      ok: false,
      status: 0,
      error: signal.aborted ? 'cancelled' : 'interrupted',
      message: signal.aborted
        ? `Upload of "${file.name}" was cancelled. Nothing was saved.`
        : `Upload of "${file.name}" was interrupted. Nothing was saved. Please try again.`,
    }
  }
}

/** A signed URL for one file, minted for the plugin. */
export async function fileUrl(pluginId: string, scope: string, path: string, download: boolean): Promise<FilesResult> {
  return filesOp(pluginId, scope, 'url', { path, download })
}

/** Fetch a file's bytes (for a plugin that must parse them, e.g. a PDF). */
export async function fileBytes(pluginId: string, scope: string, path: string): Promise<FilesResult & { bytes?: ArrayBuffer }> {
  const u = await fileUrl(pluginId, scope, path, false)
  if (!u.ok) return u
  try {
    const resp = await fetch((u.data as { url: string }).url)
    if (!resp.ok) return { ok: false, status: resp.status, error: 'unavailable', message: 'This file could not be opened.' }
    const len = Number(resp.headers.get('Content-Length') ?? '0')
    if (len > MAX_BYTES_TRANSFER) {
      return { ok: false, status: 413, error: 'too_large', message: 'This file is too large to preview. Download it instead.' }
    }
    const bytes = await resp.arrayBuffer()
    if (bytes.byteLength > MAX_BYTES_TRANSFER) {
      return { ok: false, status: 413, error: 'too_large', message: 'This file is too large to preview. Download it instead.' }
    }
    return { ok: true, status: 200, bytes }
  } catch {
    return { ok: false, status: 0, error: 'network', message: 'This file could not be opened.' }
  }
}

/** Start a browser download in the TOP document (the sandbox has no allow-downloads). */
export async function downloadFile(pluginId: string, scope: string, path: string, doc: Document = document): Promise<FilesResult> {
  const u = await fileUrl(pluginId, scope, path, true)
  if (!u.ok) return u
  const a = doc.createElement('a')
  a.href = (u.data as { url: string }).url
  a.rel = 'noopener'
  a.download = ''
  doc.body.appendChild(a)
  a.click()
  a.remove()
  return { ok: true, status: 200 }
}
